import { FEATURE_FLAGS } from '@/shared/constants/backend-api'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import { ApiResponse } from '@/shared/types/backend-response.type'

export interface FeatureFlagUser {
  id: number
  fullname: string | null
  email: string | null
  role?: {
    id: number
    name: string
  } | null
}

export interface FeatureFlag {
  id: number
  key: string
  name: string
  description?: string | null
  isEnabled: boolean
  enabledForAll: boolean
  userCount?: number
  users?: FeatureFlagUser[]
  createdAt: string
  updatedAt: string
}

export interface SearchUserResult {
  id: number
  fullname: string | null
  email: string | null
  role: string | null
}

export async function evaluateFeatureFlags(): Promise<ApiResponse<Record<string, boolean>>> {
  return authFetchJson<Record<string, boolean>>(FEATURE_FLAGS.evaluate)
}

export async function checkFeatureFlag(key: string): Promise<ApiResponse<{ key: string; isEnabled: boolean }>> {
  return authFetchJson<{ key: string; isEnabled: boolean }>(FEATURE_FLAGS.check(key))
}

export async function getAllFeatureFlags(): Promise<ApiResponse<FeatureFlag[]>> {
  return authFetchJson<FeatureFlag[]>(FEATURE_FLAGS.base)
}

export async function getFeatureFlagById(id: number): Promise<ApiResponse<FeatureFlag>> {
  return authFetchJson<FeatureFlag>(FEATURE_FLAGS.byId(id))
}

export async function createFeatureFlag(data: {
  key: string
  name: string
  description?: string
  isEnabled?: boolean
  enabledForAll?: boolean
}): Promise<ApiResponse<FeatureFlag>> {
  return authFetchJson<FeatureFlag>(FEATURE_FLAGS.base, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateFeatureFlag(
  id: number,
  data: {
    name?: string
    description?: string
    isEnabled?: boolean
    enabledForAll?: boolean
  },
): Promise<ApiResponse<FeatureFlag>> {
  return authFetchJson<FeatureFlag>(FEATURE_FLAGS.byId(id), {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteFeatureFlag(id: number): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return authFetchJson<{ success: boolean; message: string }>(FEATURE_FLAGS.byId(id), {
    method: 'DELETE',
  })
}

export async function addUserToFeatureFlag(flagId: number, userId: number): Promise<ApiResponse<FeatureFlag>> {
  return authFetchJson<FeatureFlag>(FEATURE_FLAGS.users(flagId, userId), {
    method: 'POST',
  })
}

export async function removeUserFromFeatureFlag(flagId: number, userId: number): Promise<ApiResponse<FeatureFlag>> {
  return authFetchJson<FeatureFlag>(FEATURE_FLAGS.users(flagId, userId), {
    method: 'DELETE',
  })
}

export async function searchUsersForFeatureFlag(query?: string): Promise<ApiResponse<SearchUserResult[]>> {
  const url = query ? `${FEATURE_FLAGS.searchUsers}?q=${encodeURIComponent(query)}` : FEATURE_FLAGS.searchUsers
  return authFetchJson<SearchUserResult[]>(url)
}
